import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft, Plus, Minus, ShoppingBag, Loader2, Package, CheckCircle2, XCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { getInfo, getProduto, fmtPreco } from './lojaApi'
import LojaLayout from './LojaLayout'
import { useCarrinho } from './useCarrinho'

export default function LojaProduto() {
  const { slug = '', sku = '' } = useParams()
  const navigate = useNavigate()
  const [qtd, setQtd] = useState(1)
  const [fotoAtivaIdx, setFotoAtivaIdx] = useState(0)

  const { data: info } = useQuery({
    queryKey: ['loja-info', slug],
    queryFn: () => getInfo(slug),
    enabled: !!slug,
  })

  const { data: produto, isLoading, isError } = useQuery({
    queryKey: ['loja-produto', slug, sku],
    queryFn: () => getProduto(slug, sku),
    enabled: !!slug && !!sku,
    retry: false,
  })

  const addCarrinho = useCarrinho((s) => s.adicionar)

  if (isLoading) {
    return (
      <LojaLayout info={info || null} slug={slug}>
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 text-center text-ink-500">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
          <div className="text-sm">Carregando produto...</div>
        </div>
      </LojaLayout>
    )
  }

  if (isError || !produto) {
    return (
      <LojaLayout info={info || null} slug={slug}>
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-12">
          <div className="card card-body text-center py-12 text-ink-500">
            <XCircle size={32} className="mx-auto mb-2 text-ink-300" />
            <div className="font-medium text-ink-700">Produto não encontrado</div>
            <div className="text-xs mt-1 mb-4">Este item pode ter saído do catálogo.</div>
            <Link to={`/loja/${slug}`} className="btn-primary inline-flex">
              <ArrowLeft size={14} /> Voltar ao catálogo
            </Link>
          </div>
        </div>
      </LojaLayout>
    )
  }

  const fotos = produto.fotos || []
  const fotoAtiva = fotos[fotoAtivaIdx]
  const imgPrincipalUrl = fotoAtiva?.url_full || fotoAtiva?.url_medium || produto.foto

  const estoqueOk = produto.estoque >= qtd
  const totalItem = produto.preco * qtd

  function adicionarAoCarrinho() {
    if (!estoqueOk) {
      toast.error('Quantidade indisponível em estoque')
      return
    }
    addCarrinho(slug, {
      sku: produto!.sku,
      descricao: produto!.descricao,
      preco: produto!.preco,
      unidade: produto!.unidade,
      quantidade: qtd,
      foto: produto!.foto,
      estoqueDisponivel: produto!.estoque,
    })
    toast.success(`${qtd} ${produto!.unidade} de ${produto!.descricao} no carrinho`, {
      icon: '🛍️',
      duration: 3000,
    })
  }

  return (
    <LojaLayout info={info || null} slug={slug}>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn-ghost text-sm mb-4"
        >
          <ArrowLeft size={14} /> Voltar
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* COLUNA IMAGEM */}
          <div>
            <div className="card overflow-hidden">
              <div className="aspect-square bg-ink-50 relative">
                {imgPrincipalUrl ? (
                  <img
                    src={imgPrincipalUrl}
                    alt={produto.descricao}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-ink-300">
                    <Package size={64} />
                  </div>
                )}
              </div>
            </div>

            {fotos.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {fotos.map((f, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setFotoAtivaIdx(i)}
                    className={`aspect-square rounded-md overflow-hidden border-2 transition ${
                      i === fotoAtivaIdx
                        ? 'border-brand-600'
                        : 'border-ink-200 hover:border-ink-300'
                    }`}
                  >
                    <img src={f.url_thumb} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* COLUNA INFO + ACAO */}
          <div className="space-y-4">
            {produto.categoria_nome && (
              <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">
                {produto.categoria_nome}
              </div>
            )}

            <h1 className="text-2xl md:text-3xl font-semibold text-ink-900 leading-tight">
              {produto.descricao}
            </h1>

            <div className="text-xs font-mono uppercase tracking-wider text-ink-400">
              SKU {produto.sku}
              {produto.ean && <> · EAN {produto.ean}</>}
            </div>

            {/* Card de preco */}
            <div className="card card-body">
              <div className="flex items-baseline gap-1.5">
                <span className="text-sm text-ink-500">R$</span>
                <span className="text-3xl md:text-4xl font-bold text-ink-900">{fmtPreco(produto.preco)}</span>
                <span className="text-sm text-ink-500 ml-1">/ {produto.unidade}</span>
              </div>

              <div className="mt-3 flex items-center gap-2">
                {produto.disponivel ? (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                    <CheckCircle2 size={12} />
                    Em estoque · {produto.estoque} {produto.unidade} disponíveis
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
                    <XCircle size={12} /> Sem estoque
                  </span>
                )}
              </div>
            </div>

            {/* Qtd + adicionar */}
            <div className="card card-body">
              <label className="label">Quantidade</label>
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center border border-ink-200 rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setQtd(q => Math.max(1, q - 1))}
                    disabled={qtd <= 1}
                    className="w-10 h-10 flex items-center justify-center text-ink-600 hover:bg-ink-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Minus size={14} />
                  </button>
                  <div className="w-14 text-center font-mono font-semibold tabular-nums">{qtd}</div>
                  <button
                    type="button"
                    onClick={() => setQtd(q => q + 1)}
                    className="w-10 h-10 flex items-center justify-center text-ink-600 hover:bg-ink-50"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                <div className="text-sm text-ink-600">
                  Subtotal: <strong className="text-ink-900">R$ {fmtPreco(totalItem)}</strong>
                </div>
              </div>

              <button
                type="button"
                onClick={adicionarAoCarrinho}
                disabled={!estoqueOk}
                className="btn-primary w-full justify-center"
              >
                <ShoppingBag size={16} />
                {estoqueOk ? 'Adicionar ao carrinho' : 'Indisponível'}
              </button>
            </div>

            {/* Meta — entrega / retirada */}
            <div className="card card-body">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {info?.permite_entrega && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-0.5">Entrega</div>
                    <div className="text-ink-800">
                      {info.taxa_entrega_fixa
                        ? `R$ ${fmtPreco(info.taxa_entrega_fixa)}`
                        : 'A combinar'}
                    </div>
                  </div>
                )}
                {info?.permite_retirada && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-0.5">Retirada</div>
                    <div className="text-ink-800">Disponível na loja</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-0.5">Unidade</div>
                  <div className="text-ink-800">{produto.unidade}</div>
                </div>
                {produto.peso_liquido && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-0.5">Peso líquido</div>
                    <div className="text-ink-800">{produto.peso_liquido} kg</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </LojaLayout>
  )
}
